import React, { createContext, useContext, useReducer, useEffect } from 'react';

// localStorage key
const STORAGE_KEY = 'quoteAppState';

// Helper functions for localStorage
const saveToStorage = (state) => {
  try {
    // Only save persistent data, exclude temporary states
    const persistentState = {
      quoteData: state.quoteData,
      customerData: state.customerData,
      currentStep: state.currentStep,
      isModalOpen: state.isModalOpen,
    };
    const serializedState = JSON.stringify(persistentState);
    localStorage.setItem(STORAGE_KEY, serializedState);
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

const loadFromStorage = () => {
  try {
    const serializedState = localStorage.getItem(STORAGE_KEY);
    if (serializedState === null) {
      return null;
    }
    const parsed = JSON.parse(serializedState);
    return parsed;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return null;
  }
};

// Initial state
const initialState = {
  quoteData: null,
  customerData: null,
  loading: false,
  error: null,
  isModalOpen: false,
  currentStep: 0,
};

// Action types
const SET_QUOTE_DATA = 'SET_QUOTE_DATA';
const SET_CUSTOMER_DATA = 'SET_CUSTOMER_DATA';
const SET_LOADING = 'SET_LOADING';
const SET_ERROR = 'SET_ERROR';
const SET_MODAL_OPEN = 'SET_MODAL_OPEN';
const SET_CURRENT_STEP = 'SET_CURRENT_STEP';
const RESET = 'RESET';
const LOAD_FROM_STORAGE = 'LOAD_FROM_STORAGE';

// Reducer function
const quoteReducer = (state, action) => {
  switch (action.type) {
    case SET_QUOTE_DATA:
      return { ...state, quoteData: action.payload };
    case SET_CUSTOMER_DATA:
      return { ...state, customerData: action.payload };
    case SET_LOADING:
      return { ...state, loading: action.payload };
    case SET_ERROR:
      return { ...state, error: action.payload };
    case SET_MODAL_OPEN:
      return { ...state, isModalOpen: action.payload };
    case SET_CURRENT_STEP:
      return { ...state, currentStep: action.payload };
    case LOAD_FROM_STORAGE:
      return { ...state, ...action.payload };
    case RESET:
      return { ...initialState };
    default:
      return state;
  }
};

// Create context
const QuoteContext = createContext();

// Provider component
export const QuoteProvider = ({ children }) => {
  const [state, dispatch] = useReducer(quoteReducer, initialState);

  // Load from localStorage on mount
  useEffect(() => {
    const savedState = loadFromStorage();
    if (savedState) {
      dispatch({ type: LOAD_FROM_STORAGE, payload: savedState });
    }
  }, []);

  // Save to localStorage whenever persistent state changes
  useEffect(() => {
    // Don't save on initial render or when loading
    if (state.loading) return;

    saveToStorage(state);
  }, [
    state.quoteData,
    state.customerData,
    state.currentStep,
    state.isModalOpen,
  ]);

  // Actions
  const setQuoteData = (data) => {
    dispatch({ type: SET_QUOTE_DATA, payload: data });
  };

  const setCustomerData = (data) => {
    dispatch({ type: SET_CUSTOMER_DATA, payload: data });
  };

  const setLoading = (isLoading) => {
    dispatch({ type: SET_LOADING, payload: isLoading });
  };

  const setError = (error) => {
    dispatch({ type: SET_ERROR, payload: error });
  };

  const setModalOpen = (isOpen) => {
    dispatch({ type: SET_MODAL_OPEN, payload: isOpen });
  };

  const setCurrentStep = (step) => {
    dispatch({ type: SET_CURRENT_STEP, payload: step });
  };

  const reset = () => {
    dispatch({ type: RESET });
    // Clear localStorage when resetting
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  };

  // Additional helper to manually clear storage
  const clearStorage = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  };

  // Debug helper to check current localStorage
  const getStorageData = () => {
    return loadFromStorage();
  };

  // Value to be provided by the context
  const value = {
    ...state,
    setQuoteData,
    setCustomerData,
    setLoading,
    setError,
    setModalOpen,
    setCurrentStep,
    reset,
    clearStorage,
    getStorageData, // For debugging
  };

  return (
    <QuoteContext.Provider value={value}>{children}</QuoteContext.Provider>
  );
};

// Custom hook to use the quote context
export const useQuote = () => {
  const context = useContext(QuoteContext);
  if (!context) {
    throw new Error('useQuote must be used within a QuoteProvider');
  }
  return context;
};

export default QuoteContext;
